import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { adminRentals } from "@/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_CLS = {
  new: "bg-sky-400/20 text-sky-200 border-sky-300/40",
  contacted: "bg-amber-400/20 text-amber-200 border-amber-400/40",
  "in-progress": "bg-[#F57C00]/25 text-orange-200 border-[#F57C00]/50",
  closed: "bg-slate-500/20 text-slate-400 border-slate-500/40 line-through",
};

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function CalendarTab() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [bookings, setBookings] = useState([]);

  const load = useCallback(() => {
    adminRentals().then(setBookings).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const { y, m } = cursor;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const blanks = (new Date(y, m, 1).getDay() + 6) % 7;
  const todayIso = iso(now.getFullYear(), now.getMonth(), now.getDate());

  const move = (dir) => {
    setCursor(({ y, m }) => {
      const d = new Date(y, m + dir, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const monthBookings = useMemo(() => {
    const first = iso(y, m, 1);
    const last = iso(y, m, daysInMonth);
    return bookings.filter((b) => b.start_date <= last && b.end_date >= first);
  }, [bookings, y, m, daysInMonth]);

  const forDay = (d) => {
    const day = iso(y, m, d);
    return bookings.filter((b) => b.start_date <= day && b.end_date > day);
  };

  return (
    <div data-testid="calendar-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-black text-white flex items-center gap-3">
          <CalendarDays size={24} className="text-[#F57C00]" />
          {MONTHS[m]} {y}
        </h2>
        <div className="flex gap-2">
          <button data-testid="calendar-prev" onClick={() => move(-1)} className="p-2.5 rounded-md border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] transition-colors" aria-label="Previous month">
            <ChevronLeft size={18} />
          </button>
          <button data-testid="calendar-today" onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })} className="px-4 py-2.5 rounded-md border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] transition-colors text-xs font-bold">
            Today
          </button>
          <button data-testid="calendar-next" onClick={() => move(1)} className="p-2.5 rounded-md border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] transition-colors" aria-label="Next month">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden mb-8">
        <div className="grid grid-cols-7 border-b border-sky-300/10">
          {DOW.map((d) => (
            <div key={d} className="text-center text-[10px] uppercase tracking-widest text-sky-300 font-bold py-3">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="calendar-grid">
          {Array.from({ length: blanks }).map((_, i) => (
            <div key={`b${i}`} className="min-h-[90px] border-b border-r border-white/5 bg-[#0B1F4E]/40" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const d = i + 1;
            const dayBookings = forDay(d);
            const isToday = iso(y, m, d) === todayIso;
            return (
              <div
                key={d}
                data-testid={`calendar-day-${iso(y, m, d)}`}
                className={`min-h-[90px] border-b border-r border-white/5 p-2 ${isToday ? "bg-[#F57C00]/10" : ""}`}
              >
                <p className={`text-xs font-bold mb-1.5 ${isToday ? "text-[#F57C00]" : "text-slate-400"}`}>{d}</p>
                <div className="space-y-1">
                  {dayBookings.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`calendar-booking-${b.id}`}
                      title={`${b.vehicle_name} — ${b.full_name} (${b.reference})`}
                      className={`text-[10px] font-semibold border rounded px-1.5 py-1 truncate ${STATUS_CLS[b.status] || STATUS_CLS.new}`}
                    >
                      {(b.vehicle_name || "Vehicle").split(" ").slice(-2).join(" ")} · {b.full_name.split(" ")[0]}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-6" data-testid="calendar-month-list">
        <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-4">
          Bookings touching {MONTHS[m]} ({monthBookings.length})
        </p>
        {monthBookings.length === 0 ? (
          <p className="text-sm text-slate-500">No rentals booked this month.</p>
        ) : (
          <div className="space-y-2">
            {monthBookings.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 text-sm bg-[#0B1F4E] border border-sky-300/10 rounded-md px-4 py-3">
                <span className="font-bold text-white">{b.vehicle_name}</span>
                <span className="text-slate-400">{b.full_name} · {b.reference}</span>
                <span className="text-slate-300">{b.start_date} → {b.end_date}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2.5 py-1 ${STATUS_CLS[b.status] || STATUS_CLS.new}`}>{b.status}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-4">Tip: set a rental's status to "Closed" in the Leads tab when the vehicle is returned — its dates open up for new bookings.</p>
      </div>
    </div>
  );
}
